import * as React from 'react';
import { createBrowserRouter, LoaderFunctionArgs, redirect } from 'react-router';
import Loader from 'components/Loader';
import ErrorBoundary from './ErrorBoundary';
import { CustomerFacingError } from 'utils/ui/errors';
import { customerRoutes } from './routes/customer';
import { dealerRoutes } from './routes/dealer';
import { loadPublicSession } from 'state/session';
import { isValidSessionID } from 'utils/session';
import { EErrorCode } from 'config/errors-codes';
import { getStorageItem } from 'utils/ui/storage';

const Auth = React.lazy(() => import('components/Auth'));
const AuthResume = React.lazy(() => import('components/Auth/AuthResume'));

export const router = createBrowserRouter([
  {
    path: '/auth',
    element: (
      <React.Suspense fallback={<Loader />}>
        <Auth />
      </React.Suspense>
    ),
  },
  {
    path: '/auth/resume',
    element: (
      <React.Suspense fallback={<Loader />}>
        <AuthResume />
      </React.Suspense>
    ),
  },
  {
    path: '/:sessionId',
    element: <React.Fragment />,
    errorElement: <ErrorBoundary />,
    loader: universalSessionLoader,
  },
  {
    path: '/',
    hydrateFallbackElement: <></>,
    errorElement: <ErrorBoundary />,
    children: [...dealerRoutes, ...customerRoutes],
  },
  {
    path: '*',
    errorElement: <ErrorBoundary />,
    loader: async () => {
      throw new CustomerFacingError(404);
    },
  },
]);

/**
 * Universal links are used for sharing. It's agnostic of the user type so it will try to detect if used by
 * dealer or customer. Dealers are expected to have a special dealer flag in localstorage.
 * If dealer detected we redirect to dealer-specific URL and authentication may be prompted.
 *
 */
async function universalSessionLoader({ params }: LoaderFunctionArgs) {
  const { sessionId } = params;

  // If not a valid UUID, throw a Response that won't be caught by error boundary
  // This allows the router to continue matching other routes
  if (!sessionId || !isValidSessionID(sessionId)) {
    // this response should trigger React Router to try another route before giving up
    throw new Response('Not Found', {
      status: 404,
      statusText: 'Not Found',
    });
  }

  try {
    // Load session data to get brand and market
    const sessionData = await loadPublicSession(sessionId);

    if (!sessionData || !sessionData.brand || !sessionData.market) {
      throw new CustomerFacingError(EErrorCode.CANT_LOAD_SESSION);
    }

    const { brand, market } = sessionData;

    // Check if user is a dealer based on localStorage flag
    const isDealer = getStorageItem('localStorage', 'dealer') === 'true';

    if (isDealer) {
      // Redirect dealers to the dealer interface
      return redirect(`/${brand}/${market}/${sessionId}`);
    } else {
      // Redirect customers to the customer viewing interface
      return redirect(`/${brand}/${market}/view/${sessionId}`);
    }
  } catch (error) {
    // If it's already a Response (like our 404), re-throw it
    if (error instanceof Response) {
      throw error;
    }
    // If session loading fails, throw appropriate error
    throw new CustomerFacingError(EErrorCode.CANT_LOAD_SESSION);
  }
}
